import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, message, Row } from 'antd';
import { useEffect, useState } from 'react';
import { ButtonType, TAButton } from '../../../components';
import { ComponentGuard } from '../../../components/component-guard';
import { SectionTitle, TATabs, Title } from '../../../components/dashboard/styles';
import { TeamMemberInvitationForm } from '../../../components/team-member-invitation-form/team-member-invitation-form';
import teamMembersService from '../../../services/team-members.service';
import RolePermissions from './role-permissions';
import TeamMembersInvitations from './team-members-invitations';
import TeamMembersList from './team-members-list';

import { FaLock, FaLockOpen } from 'react-icons/fa';
import { JobControlledAccessModal } from '../../../components/job-controlled-access-modal';
import authService from '../../../services/auth.service';
import companyService from '../../../services/company.service';
import { notifyError, notifySuccess } from '../../../utils/notification';
import { Role } from '../../../enums/role';

const TeamMembers: React.FC<any> = () => {
	const [allMembers, setAllMembers] = useState<any>([]);
	const [activeMembers, setActiveMembers] = useState<any>([]);
	const [deactivatedMembers, setDeactiveMembers] = useState<any>([]);
	const [invitations, setInvitations] = useState<any>([]);
	const [memberInvitationVisible, setMemberInvitationVisible] = useState<boolean>(false);
	const [membersLoading, setMembersLoading] = useState<boolean>(false);
	const [invitationsLoading, setInvitationsLoading] = useState<boolean>(false);
	const [rolePermissionsModalVisible, setRolePermissionsModalVisible] = useState<boolean>(false);
	const [controlledAccessModalVisible, setControlledAccessModalVisible] = useState<boolean>(false);
	const [controlledAccessEnabled, setControlledAccessEnabled] = useState<boolean>(false);

	useEffect(() => {
		setMembersLoading(true);
		setInvitationsLoading(true);

		teamMembersService.getTeamMembers().then(resp => {
			setAllMembers(resp.data);
			setMembersLoading(false);
		});
		teamMembersService.getTeamMembersInvitations().then(resp => {
			setInvitations(resp.data);
			setInvitationsLoading(false);
		});
		if (authService.hasRoles(Role.COMPANY_OWNER)) {
			authService.getCompanyProfile().then(company => setControlledAccessEnabled(company.jobsControlledAccess));
		}
	}, []);

	useEffect(() => {
		setMembersLoading(true);
		setActiveMembers(allMembers.filter(m => m.activeMember));
		setDeactiveMembers(allMembers.filter(m => !m.activeMember));
		setMembersLoading(false);
	}, [allMembers]);

	const inviteHandler = async invitation => {
		setInvitations([...invitations, invitation]);
		setMemberInvitationVisible(false);
	};

	const rolePermissionsHandler = e => {
		e.preventDefault();
		setRolePermissionsModalVisible(true);
	};

	const changeActiveStateHandler = (member, response) => {
		allMembers[allMembers.findIndex(e => e.id === member.memberId)].activeMember = response.data.activeMember;
		setAllMembers([...allMembers]);
	};

	const changeRoleHandler = (member, response) => {
		allMembers[allMembers.findIndex(e => e.id === member.memberId)].role = response.data.role;
		setAllMembers([...allMembers]);
	};

	const onJobControlledAccessEnable = async () => {
		try {
			const resp = await companyService.setJobsAccess(true);
			if (!resp || !resp.success) notifyError('Something went wrong!');
			setControlledAccessEnabled(true);
			notifySuccess('Jobs Access Controll Enabled Successfully!');
		} catch (e: any) {
			notifyError(e.message);
		}
	};

	const onJobControlledAccessDisable = async () => {
		try {
			const resp = await companyService.setJobsAccess(false);
			if (!resp || !resp.success) notifyError('Something went wrong!');
			setControlledAccessEnabled(false);
			notifySuccess('Jobs Access Controll Disabled Successfully!');
		} catch (e: any) {
			notifyError(e.message);
		}
	};

	return (
		<div className="dashboard" style={{ width: '95%' }} id="dashboard">
			<Row gutter={24} className="justify-content-between mx-auto mb-4">
				<Col xs={{ span: 24 }} className="text-start page-label">
					<SectionTitle>Team Members</SectionTitle>
				</Col>

				<Col
					className="mt-3"
					xs={{ span: 24 }}
					md={{ span: 14 }}
					xxl={{ span: authService.hasRoles(Role.COMPANY_OWNER) ? 16 : 20 }}
				>
					<a
						href=""
						className="d-flex align-items-center"
						style={{ fontSize: '16px', width: 'fit-content' }}
						onClick={rolePermissionsHandler}
					>
						<InfoCircleOutlined className="me-1" />
						View role permissions
					</a>
				</Col>

				<Col xs={{ span: 24 }} md={{ span: 5 }} xxl={{ span: 4 }}>
					<ComponentGuard
						allowed={authService.hasRoles(Role.COMPANY_OWNER, Role.COMPANY_ADMIN)}
						fallback={undefined}
					>
						<TAButton
							onClick={() => setMemberInvitationVisible(true)}
							btntype={ButtonType.PRIMARY}
							className="d-flex align-items-center"
							style={{ padding: '11px 20px', height: 'auto', fontWeight: 600 }}
						>
							<Title icon={<PlusOutlined />}>Invite a new member</Title>
						</TAButton>
					</ComponentGuard>
				</Col>
				<Col xs={24} md={{ span: 5 }} xxl={{ span: 4 }}>
					<ComponentGuard allowed={authService.hasRoles(Role.COMPANY_OWNER)}>
						<TAButton
							btntype={controlledAccessEnabled ? ButtonType.PRIMARY : ButtonType.SECONDARY}
							style={{ padding: '11px 20px', height: 'auto', fontWeight: 600 }}
							onClick={() => setControlledAccessModalVisible(true)}
						>
							<Title
								icon={
									controlledAccessEnabled ? (
										<FaLock className="me-1" />
									) : (
										<FaLockOpen className="me-1" />
									)
								}
							>
								Controlled Access
							</Title>
						</TAButton>
					</ComponentGuard>
				</Col>
			</Row>

			<TATabs>
				<TATabs.TabPane tab={`Active Members (${activeMembers.length})`} key="users">
					<div className="dashboard-content" id="dashboard-content">
						<TeamMembersList
							onChangeActiveState={changeActiveStateHandler}
							onChangeRole={changeRoleHandler}
							data={activeMembers}
							loading={membersLoading}
							controlledAccessEnabled={controlledAccessEnabled}
						/>
					</div>
				</TATabs.TabPane>
				<TATabs.TabPane tab={`Invitations (${invitations.length})`} key="invitations">
					<div className="dashboard-content" id="dashboard-content">
						<TeamMembersInvitations
							data={invitations}
							onInvitationResend={invitation => {
								setInvitations(invitations.map(i => (i.id === invitation.id ? invitation : i)));
								message.success('Invitation resend successfully!');
							}}
							loading={invitationsLoading}
						/>
					</div>
				</TATabs.TabPane>

				<TATabs.TabPane tab={`Deactivated Members (${deactivatedMembers.length})`} key="Deactivated Users">
					<div className="dashboard-content" id="dashboard-content">
						<TeamMembersList
							onChangeActiveState={changeActiveStateHandler}
							data={deactivatedMembers}
							loading={membersLoading}
						/>
					</div>
				</TATabs.TabPane>
			</TATabs>

			<RolePermissions
				visible={rolePermissionsModalVisible}
				onCancel={() => setRolePermissionsModalVisible(false)}
			/>
			<TeamMemberInvitationForm
				visible={memberInvitationVisible}
				onCancel={() => setMemberInvitationVisible(false)}
				onAdd={inviteHandler}
			/>
			{authService.hasRoles(Role.COMPANY_OWNER) ? (
				<JobControlledAccessModal
					visible={controlledAccessModalVisible}
					onEnable={onJobControlledAccessEnable}
					onDisable={onJobControlledAccessDisable}
					enabled={controlledAccessEnabled}
					onCancel={() => setControlledAccessModalVisible(false)}
				/>
			) : null}
		</div>
	);
};

export default TeamMembers;
